import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMediaById } from "@/lib/media";
import { getTypeLabel } from "@/lib/labels";

interface Slide {
  id: string;
  image: string;
  tagline: string;
}

const slides: Slide[] = [
  {
    id: "movie-19",
    image:
      "https://readdy.ai/api/search-image?query=atmospheric%20cinematic%20background%20of%20a%20lone%20glowing%20lantern%20in%20a%20war-torn%20town%20at%20dawn%2C%20deep%20indigo%20violet%20sky%20with%20warm%20orange%20light%20glow%2C%20stylized%20painterly%20illustration%2C%20wide%20landscape%2C%20dark%20moody%20tones%20ideal%20for%20text%20overlay&width=1600&height=900&seq=hero-01&orientation=landscape",
    tagline: "战火中的一盏灯，照亮长夜尽头",
  },
  {
    id: "movie-03",
    image:
      "https://readdy.ai/api/search-image?query=atmospheric%20cinematic%20background%20of%20an%20ancient%20chinese%20palace%20under%20a%20violet%20dusk%20sky%20with%20distant%20torches%2C%20stylized%20painterly%20illustration%2C%20wide%20landscape%2C%20dark%20moody%20tones%20ideal%20for%20text%20overlay&width=1600&height=900&seq=hero-02&orientation=landscape",
    tagline: "一代寒门书生的朝堂风云录",
  },
  {
    id: "tv-02",
    image:
      "https://readdy.ai/api/search-image?query=atmospheric%20cinematic%20background%20of%20a%20grand%20throne%20hall%20lit%20by%20many%20candles%20in%20deep%20purple%20shadows%2C%20stylized%20painterly%20illustration%2C%20wide%20landscape%2C%20dark%20moody%20tones%20ideal%20for%20text%20overlay&width=1600&height=900&seq=hero-03&orientation=landscape",
    tagline: "权谋之巅，谁主沉浮",
  },
  {
    id: "movie-01",
    image:
      "https://readdy.ai/api/search-image?query=atmospheric%20cinematic%20background%20of%20a%20vast%20nebula%20and%20distant%20planet%20seen%20from%20space%2C%20deep%20indigo%20violet%20cosmos%20with%20warm%20orange%20star%20glow%2C%20stylized%20painterly%20illustration%2C%20wide%20landscape%2C%20dark%20moody%20tones%20ideal%20for%20text%20overlay&width=1600&height=900&seq=hero-04&orientation=landscape",
    tagline: "穿越星际，寻找人类的下一个答案",
  },
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);

  const goTo = useCallback((index: number) => {
    setCurrent((index + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      aria-label="热门影视推荐"
    >
      <div className="relative h-[420px] w-full md:h-[600px]">
        {slides.map((slide, index) => {
          const item = getMediaById(slide.id);
          if (!item) return null;
          return (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === current ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
            >
              <img
                src={slide.image}
                alt={`${item.title} 焦点海报`}
                title={`${item.title} - ${item.genre}${getTypeLabel(item.type)}`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
            </div>
          );
        })}

        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-12 lg:px-20">
            <div key={current} className="animate-fade-up max-w-2xl">
              {(() => {
                const item = getMediaById(slides[current].id);
                if (!item) return null;
                return (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                        {getTypeLabel(item.type)}
                      </span>
                      <span className="rounded-md border border-background-50/40 px-2.5 py-1 text-xs text-background-50">
                        {item.genre}
                      </span>
                      <span className="flex items-center gap-1 rounded-md border border-background-50/40 px-2.5 py-1 text-xs text-background-50">
                        <i className="ri-star-fill text-[10px] text-accent-400" aria-hidden="true"></i>
                        {item.rating.toFixed(1)}
                      </span>
                    </div>
                    <h2 className="mt-4 font-heading text-3xl font-extrabold text-background-50 md:text-5xl">
                      {item.title}
                    </h2>
                    <p className="mt-2 text-sm text-accent-300 md:text-base">{slides[current].tagline}</p>
                    <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200/90 md:text-base line-clamp-3">
                      {item.description}
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <Link
                        to={`/detail/${item.id}`}
                        className="flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
                      >
                        <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                        查看详情
                      </Link>
                      <Link
                        to="/category/movie"
                        className="rounded-md border border-background-50/50 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
                      >
                        浏览更多电影
                      </Link>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => goTo(current - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:flex cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(current + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:flex cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
        </button>

        <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`切换到第 ${index + 1} 张`}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                index === current ? "w-7 bg-accent-500" : "w-2 bg-background-50/50"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}